/**
 * Centralized MSAL token manager.
 *
 * Provides a single source of truth for ID-token acquisition across all API
 * client hooks. Handles:
 *   - Proactive background refresh before expiry
 *   - Tab-visibility refresh (browsers throttle timers in background tabs)
 *   - InteractionRequiredAuthError → acquireTokenRedirect
 *   - Silent refresh with forceRefresh on 401 retry
 *   - Dev-bypass mode (no-op)
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, SilentRequest } from "@azure/msal-browser";

import { useDevBypass } from "../utils/devBypass";

const TOKEN_SCOPES = ["openid", "profile", "email"];

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry
const PROACTIVE_CHECK_MS = 60 * 1000;     // poll every 60 s

interface TokenCache {
  token: string;
  expiry: number; // epoch ms
}

export interface TokenManager {
  /** Get a valid ID token (async, refreshes if needed). */
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  /** Get the user identity claims for header construction. */
  getUserInfo: () => { userId: string; email: string; roles: string[] };
}

export function useTokenManager(): TokenManager {
  const { instance, accounts } = useMsal();
  const msalAccount = accounts[0] ?? null;
  const cacheRef = useRef<TokenCache | null>(null);
  const refreshingRef = useRef<Promise<string | null> | null>(null);

  const acquireToken = useCallback(
    async (forceRefresh: boolean): Promise<string | null> => {
      if (useDevBypass || !msalAccount) return null;

      const now = Date.now();
      if (
        !forceRefresh &&
        cacheRef.current &&
        cacheRef.current.expiry > now + REFRESH_BUFFER_MS
      ) {
        return cacheRef.current.token;
      }

      try {
        const request: SilentRequest = {
          scopes: TOKEN_SCOPES,
          account: msalAccount,
          forceRefresh,
        };
        const result = await instance.acquireTokenSilent(request);
        if (result.idToken) {
          const expiry = result.expiresOn
            ? result.expiresOn.getTime()
            : now + 3_600_000;
          cacheRef.current = { token: result.idToken, expiry };
          return result.idToken;
        }
        return null;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          cacheRef.current = null;
          try {
            await instance.acquireTokenRedirect({
              scopes: TOKEN_SCOPES,
              account: msalAccount,
            });
          } catch {
            // Redirect navigates away; catch is for edge cases
          }
          return null;
        }

        // For other errors, retry once with forceRefresh before giving up
        if (!forceRefresh) {
          try {
            const retryRequest: SilentRequest = {
              scopes: TOKEN_SCOPES,
              account: msalAccount,
              forceRefresh: true,
            };
            const retryResult = await instance.acquireTokenSilent(retryRequest);
            if (retryResult.idToken) {
              const expiry = retryResult.expiresOn
                ? retryResult.expiresOn.getTime()
                : Date.now() + 3_600_000;
              cacheRef.current = { token: retryResult.idToken, expiry };
              return retryResult.idToken;
            }
          } catch (retryErr) {
            // Retry also failed — fall through to redirect below
          }
        }

        // All silent attempts exhausted — force interactive login
        console.warn("Token acquisition failed, redirecting to login:", err);
        cacheRef.current = null;
        try {
          await instance.acquireTokenRedirect({
            scopes: TOKEN_SCOPES,
            account: msalAccount,
          });
        } catch {
          // Redirect navigates away
        }
        return null;
      }
    },
    [instance, msalAccount],
  );

  /**
   * Deduplicated token getter — concurrent callers share one in-flight
   * refresh rather than racing against each other.
   */
  const getToken = useCallback(
    async (forceRefresh = false): Promise<string | null> => {
      if (useDevBypass) return null;

      if (forceRefresh) {
        cacheRef.current = null;
        const promise = acquireToken(true);
        refreshingRef.current = promise;
        const result = await promise;
        refreshingRef.current = null;
        return result;
      }

      // Fast path: cache is still valid
      const now = Date.now();
      if (cacheRef.current && cacheRef.current.expiry > now + REFRESH_BUFFER_MS) {
        return cacheRef.current.token;
      }

      // Cache is expired or missing — force a real refresh from MSAL
      if (refreshingRef.current) {
        return refreshingRef.current;
      }

      const needsForce = cacheRef.current != null && cacheRef.current.expiry <= now;
      const promise = acquireToken(needsForce);
      refreshingRef.current = promise;
      const result = await promise;
      refreshingRef.current = null;
      return result;
    },
    [acquireToken],
  );

  // Proactive background refresh so the token never expires mid-session
  useEffect(() => {
    if (useDevBypass || !msalAccount) return;

    const timer = setInterval(() => {
      const now = Date.now();
      if (cacheRef.current && cacheRef.current.expiry <= now + REFRESH_BUFFER_MS) {
        acquireToken(false).catch(() => {});
      }
    }, PROACTIVE_CHECK_MS);

    return () => clearInterval(timer);
  }, [acquireToken, msalAccount]);

  // Refresh token when tab becomes visible again (timers don't run in background)
  useEffect(() => {
    if (useDevBypass || !msalAccount) return;

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        if (!cacheRef.current || cacheRef.current.expiry <= now + REFRESH_BUFFER_MS) {
          acquireToken(cacheRef.current ? cacheRef.current.expiry <= now : false).catch(() => {});
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [acquireToken, msalAccount]);

  const getUserInfo = useCallback(() => {
    if (useDevBypass) {
      return { userId: "dev-user-id", email: "dev@localhost", roles: ["Portal.Admin", "Portal.User"] };
    }
    if (!msalAccount) {
      return { userId: "", email: "", roles: [] as string[] };
    }
    const claims = msalAccount.idTokenClaims as Record<string, unknown> | undefined;
    return {
      userId: msalAccount.localAccountId || (claims?.oid as string) || "",
      email: msalAccount.username || "",
      roles: (claims?.roles as string[]) || [],
    };
  }, [msalAccount]);

  return useMemo(() => ({ getToken, getUserInfo }), [getToken, getUserInfo]);
}

export default useTokenManager;
